import type {
  ILogger,
  IRealtimeClient,
  RealtimeEventKey,
  RealtimeEventMap,
  RealtimeHandler,
  RealtimeStatus,
  Unsubscribe,
} from "@crialume/sdk";

type AnyHandler = (payload: unknown) => void;

/** Cliente controlado pelo kernel: abre a conexão no login e fecha no logout. */
export interface ManagedRealtimeClient extends IRealtimeClient {
  /** Abre (ou mantém) a conexão. Nunca rejeita: falhas viram novas tentativas. */
  start(): Promise<void>;
  /** Fecha a conexão e cancela novas tentativas. Nunca rejeita. */
  stop(): Promise<void>;
}

/**
 * Base dos clientes de tempo real: inscrições por evento, despacho isolado
 * (um handler com erro não impede os outros) e status observável.
 */
export class RealtimeEmitter implements IRealtimeClient {
  private readonly handlers = new Map<string, Set<AnyHandler>>();
  private readonly statusListeners = new Set<(status: RealtimeStatus) => void>();
  private status: RealtimeStatus = "disconnected";

  constructor(
    protected readonly logger?: ILogger,
    /** Chamado na primeira inscrição de cada evento (registro no transporte). */
    private readonly onFirstSubscription?: (event: string) => void,
  ) {}

  getStatus = (): RealtimeStatus => this.status;

  subscribeStatus = (listener: (status: RealtimeStatus) => void): Unsubscribe => {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  };

  on = <K extends RealtimeEventKey>(event: K, handler: RealtimeHandler<K>): Unsubscribe => {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
      this.onFirstSubscription?.(event);
    }
    set.add(handler as AnyHandler);
    return () => {
      set.delete(handler as AnyHandler);
    };
  };

  /** Eventos já assinados ao menos uma vez. */
  protected subscribedEvents(): string[] {
    return [...this.handlers.keys()];
  }

  protected dispatch(event: string, payload: unknown): void {
    for (const handler of [...(this.handlers.get(event) ?? [])]) {
      try {
        handler(payload);
      } catch (error) {
        this.logger?.error(`handler de tempo real "${event}" falhou`, error);
      }
    }
  }

  protected setStatus(status: RealtimeStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const listener of [...this.statusListeners]) {
      try {
        listener(status);
      } catch (error) {
        this.logger?.error("listener de status do tempo real falhou", error);
      }
    }
  }
}

/** Cliente local, sem rede: modo mock, testes e ambientes sem hub. */
export interface LocalRealtimeClient extends ManagedRealtimeClient {
  /** Entrega um evento aos inscritos como se viesse do servidor. */
  emit<K extends RealtimeEventKey>(event: K, payload: RealtimeEventMap[K]): void;
  setStatus(status: RealtimeStatus): void;
}

/**
 * Sem conexão: o status fica `disconnected` (telas caem para polling), mas
 * `emit` permite simular eventos do servidor em mocks e testes.
 */
export function createLocalRealtimeClient(options: { logger?: ILogger } = {}): LocalRealtimeClient {
  class Local extends RealtimeEmitter implements LocalRealtimeClient {
    start = async (): Promise<void> => {};
    stop = async (): Promise<void> => {};
    emit = <K extends RealtimeEventKey>(event: K, payload: RealtimeEventMap[K]): void => {
      this.dispatch(event, payload);
    };
    override setStatus(status: RealtimeStatus): void {
      super.setStatus(status);
    }
  }
  return new Local(options.logger);
}

/**
 * Visão por módulo: compartilha a conexão, mas rastreia as inscrições do
 * módulo para removê-las no unload (mesmo padrão do `createScopedBus`).
 */
export function createScopedRealtime(client: IRealtimeClient): {
  realtime: IRealtimeClient;
  dispose: () => void;
} {
  const subscriptions = new Set<Unsubscribe>();
  const track = (off: Unsubscribe): Unsubscribe => {
    const wrapped = () => {
      off();
      subscriptions.delete(wrapped);
    };
    subscriptions.add(wrapped);
    return wrapped;
  };

  return {
    realtime: {
      getStatus: () => client.getStatus(),
      subscribeStatus: (listener) => track(client.subscribeStatus(listener)),
      on: (event, handler) => track(client.on(event, handler)),
    },
    dispose: () => {
      for (const off of [...subscriptions]) off();
    },
  };
}
